import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';
import {
  buildAppleSurfaceSnapshot,
  buildAppleTripSurfaceSnapshot,
  type AppleIndexedTripSnapshot,
  type AppleTripSurfaceSnapshotV1,
} from '@/services/applePlatformSnapshot';
import { applePlatformFlags } from '@/config/applePlatformFlags';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { NativeModules, Platform } from 'react-native';

export type NextTripWidgetPayload = {
  tripId?: string;
  title: string;
  subtitle: string;
  tapRoute: string;
  tapUrl: string;
  emptyState: boolean;
  surface: AppleTripSurfaceSnapshotV1;
};

export const buildNextTripWidgetPayload = (opts: {
  nextTrip?: Trip | null;
  itineraryItems?: ItineraryItem[];
  now?: Date;
}): NextTripWidgetPayload => {
  const snapshot = buildAppleSurfaceSnapshot({
    nextTrip: opts.nextTrip ?? null,
    itineraryItems: opts.itineraryItems ?? [],
    now: opts.now,
  });
  if (!snapshot.nextTrip) {
    return {
      title: 'Plan your next trip',
      subtitle: 'Open Trips to get started',
      tapRoute: '/(tabs)',
      tapUrl: snapshot.tripSurface.tapUrl,
      emptyState: true,
      surface: snapshot.tripSurface,
    };
  }
  return {
    tripId: snapshot.nextTrip.tripId,
    title: snapshot.nextTrip.title,
    subtitle: `${snapshot.nextTrip.destinationLabel} · ${snapshot.nextTrip.countdownLabel}`,
    tapRoute: snapshot.tripSurface.planRoute,
    tapUrl: snapshot.tripSurface.tapUrl,
    emptyState: false,
    surface: snapshot.tripSurface,
  };
};

const NEXT_TRIP_WIDGET_CACHE_KEY = '@tripvio_apple_widget_next_trip_payload_v1';
const TRIP_SURFACE_SNAPSHOT_CACHE_KEY = '@tripvio_apple_trip_surface_snapshot_v1';

type TripvioNativeSurfaceModule = {
  writeTripSurfaceSnapshot?: (json: string) => Promise<void>;
  reloadTripSurfaceTimelines?: () => Promise<void>;
  indexTripSurfaceSpotlightItems?: (json: string) => Promise<void>;
  purgeTripSurfaceSpotlightItems?: () => Promise<void>;
  startOrUpdateTripLiveActivity?: (json: string) => Promise<void>;
  endTripLiveActivity?: (tripId: string) => Promise<void>;
  refreshTripSurfaceLiveActivityFromCache?: () => Promise<void>;
};

const getNativeSurfaceModule = (): TripvioNativeSurfaceModule | null => {
  if (Platform.OS !== 'ios') return null;
  const module = (NativeModules as Record<string, unknown>).TripvioTripSurfaceModule;
  if (!module || typeof module !== 'object') return null;
  return module as TripvioNativeSurfaceModule;
};

export const buildTripSurfaceWidgetPayload = (opts: {
  trip?: Trip | null;
  itineraryItems?: ItineraryItem[];
  indexedTrips?: AppleIndexedTripSnapshot[];
  now?: Date;
}): AppleTripSurfaceSnapshotV1 =>
  buildAppleTripSurfaceSnapshot({
    trip: opts.trip ?? null,
    itineraryItems: opts.itineraryItems ?? [],
    indexedTrips: opts.indexedTrips,
    now: opts.now,
  });

export const cacheNextTripWidgetPayload = async (payload: NextTripWidgetPayload): Promise<void> => {
  await AsyncStorage.setItem(NEXT_TRIP_WIDGET_CACHE_KEY, JSON.stringify(payload));
};

export const refreshTripSurfaceLiveActivityFromCache = async (): Promise<void> => {
  if (Platform.OS !== 'ios') return;
  const nativeModule = getNativeSurfaceModule();
  await nativeModule?.refreshTripSurfaceLiveActivityFromCache?.();
};

export const cacheTripSurfaceSnapshot = async (snapshot: AppleTripSurfaceSnapshotV1): Promise<void> => {
  const serialized = JSON.stringify(snapshot);
  await AsyncStorage.setItem(TRIP_SURFACE_SNAPSHOT_CACHE_KEY, serialized);

  const nativeModule = getNativeSurfaceModule();
  if (!nativeModule?.writeTripSurfaceSnapshot) {
    return;
  }
  await nativeModule.writeTripSurfaceSnapshot(serialized);
  await nativeModule.reloadTripSurfaceTimelines?.();
  if (applePlatformFlags.phase5Enabled && applePlatformFlags.spotlightEnabled) {
    if (snapshot.trip) {
      await nativeModule.indexTripSurfaceSpotlightItems?.(serialized);
    } else {
      await nativeModule.purgeTripSurfaceSpotlightItems?.();
    }
  }
  if (snapshot.liveActivity.eligible) {
    await nativeModule.startOrUpdateTripLiveActivity?.(serialized);
    return;
  }
  if (snapshot.trip?.tripId) {
    await nativeModule.endTripLiveActivity?.(snapshot.trip.tripId);
  }
};

export const purgeTripSurfaceSpotlightItems = async (): Promise<void> => {
  await AsyncStorage.removeItem(TRIP_SURFACE_SNAPSHOT_CACHE_KEY);
  const nativeModule = getNativeSurfaceModule();
  await nativeModule?.purgeTripSurfaceSpotlightItems?.();
};

export const readCachedNextTripWidgetPayload = async (): Promise<NextTripWidgetPayload | null> => {
  const raw = await AsyncStorage.getItem(NEXT_TRIP_WIDGET_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as NextTripWidgetPayload | null;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const readCachedTripSurfaceSnapshot = async (): Promise<AppleTripSurfaceSnapshotV1 | null> => {
  const raw = await AsyncStorage.getItem(TRIP_SURFACE_SNAPSHOT_CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AppleTripSurfaceSnapshotV1 | null;
    if (!parsed || typeof parsed !== 'object' || parsed.version !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const logNextTripWidgetOptIn = async (enabled: boolean, source: 'prompt' | 'settings' | 'system') => {
  const { logAnalyticsEvent } = await import('@/services/analyticsLogger');
  await logAnalyticsEvent('apple_widget_opt_in_changed', {
    widgetKind: 'next_trip_countdown',
    enabled,
    source,
  });
};
